<template>
    <v-container fluid class="pa-4 pa-md-6">
        <div class="d-flex align-center justify-space-between mb-4 flex-wrap ga-2">
            <div>
                <h1 class="text-h5 font-weight-bold">Clean Truck Check</h1>
                <div class="text-body-2 text-medium-emphasis">California CARB emissions compliance</div>
            </div>
            <div class="d-flex ga-2">
                <v-btn variant="tonal" prepend-icon="mdi-history" @click="goSubmissions">Submissions</v-btn>
                <v-btn v-if="canEnroll" color="primary" prepend-icon="mdi-plus" @click="enrollOpen = true">Enroll assets</v-btn>
            </div>
        </div>

        <template v-if="loading">
            <v-skeleton-loader type="card" class="mb-4"/>
            <v-skeleton-loader type="table"/>
        </template>

        <template v-else>
            <v-row class="mb-2">
                <v-col cols="12" md="4">
                    <v-card rounded="lg" class="h-100">
                        <v-card-text class="d-flex flex-column align-center justify-center py-6">
                            <v-progress-circular :model-value="summary.readiness_pct" :size="132" :width="13"
                                                 :color="readinessColor">
                                <div class="text-center">
                                    <div class="text-h4 font-weight-bold">{{ summary.readiness_pct }}%</div>
                                    <div class="text-caption text-medium-emphasis">compliant</div>
                                </div>
                            </v-progress-circular>
                            <div class="text-body-2 text-medium-emphasis mt-3">
                                {{ summary.total_enrolled }} vehicles enrolled
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col cols="12" md="8">
                    <v-card rounded="lg" class="h-100">
                        <v-card-title class="text-subtitle-1 font-weight-bold">Status breakdown</v-card-title>
                        <v-card-text>
                            <v-row dense>
                                <v-col v-for="bucket in buckets" :key="bucket.key" cols="6" sm="4">
                                    <div class="d-flex align-center pa-2 rounded bucket-tile"
                                         :class="{'bucket-active': statusFilter === bucket.key}"
                                         @click="toggleBucket(bucket.key)">
                                        <v-icon :color="bucket.color" :icon="bucket.icon" class="mr-2"/>
                                        <div>
                                            <div class="text-h6 font-weight-bold">{{ bucket.count }}</div>
                                            <div class="text-caption text-medium-emphasis">{{ bucket.label }}</div>
                                        </div>
                                    </div>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-card v-if="needsAttention.length" rounded="lg" class="mb-4" variant="tonal" color="error">
                <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center">
                    <v-icon icon="mdi-alert-circle-outline" class="mr-2"/>
                    Needs attention ({{ needsAttention.length }})
                </v-card-title>
                <v-list bg-color="transparent" density="comfortable">
                    <v-list-item v-for="row in needsAttention" :key="row.asset_id"
                                 @click="goToAsset(row.asset_id)">
                        <template #prepend>
                            <v-icon :color="statusColor(row.severity)" :icon="statusIcon(row.severity)"/>
                        </template>
                        <v-list-item-title class="font-weight-medium">{{ row.asset_name || ('Asset ' + row.asset_id) }}</v-list-item-title>
                        <v-list-item-subtitle>{{ row.explanation }}</v-list-item-subtitle>
                        <template #append>
                            <v-chip size="small" :color="deadlineColor(row.days_to_deadline)" variant="flat">
                                {{ deadlineLabel(row.days_to_deadline) }}
                            </v-chip>
                        </template>
                    </v-list-item>
                </v-list>
            </v-card>

            <v-card rounded="lg">
                <v-card-text>
                    <v-alert v-if="needsDetails.length" type="warning" variant="tonal" density="compact" class="mb-3">
                        <div class="d-flex align-center justify-space-between flex-wrap ga-2">
                            <span>{{ needsDetails.length }} vehicle{{ needsDetails.length > 1 ? 's' : '' }} need CARB details (VIN, license plate, owner email).</span>
                            <v-btn size="small" variant="text" @click="detailsFilter = !detailsFilter">
                                {{ detailsFilter ? 'Show all' : 'Show these' }}
                            </v-btn>
                        </div>
                    </v-alert>
                    <div class="d-flex align-center ga-3 mb-3 flex-wrap">
                        <v-text-field v-model="search" density="compact" variant="outlined" hide-details
                                      prepend-inner-icon="mdi-magnify" placeholder="Search name, driver, VIN or plate"
                                      style="max-width: 320px"/>
                        <v-chip v-if="statusFilter" closable size="small" @click:close="statusFilter = null">
                            {{ bucketLabel(statusFilter) }}
                        </v-chip>
                        <v-spacer/>
                        <span class="text-caption text-medium-emphasis">{{ filtered.length }} of {{ rows.length }}</span>
                    </div>

                    <v-data-table :headers="headers" :items="filtered" :items-per-page="25" density="comfortable"
                                  @click:row="onRowClick" class="ctc-table">
                        <template #item.asset_name="{item}">
                            <div class="d-flex align-center ga-1">
                                <span>{{ item.asset_name || ('Asset ' + item.asset_id) }}</span>
                                <v-icon v-if="item.details_complete === false" icon="mdi-alert-circle-outline"
                                        color="warning" size="small"/>
                            </div>
                        </template>
                        <template #item.status="{item}">
                            <v-chip size="small" :color="statusColor(item.severity)" variant="tonal">
                                <v-icon start :icon="statusIcon(item.severity)" size="small"/>
                                {{ item.status_label }}
                            </v-chip>
                        </template>
                        <template #item.last_submission="{item}">
                            <span v-if="item.last_submission?.submitted_at">{{ formatDateTime(item.last_submission.submitted_at) }}</span>
                            <span v-else class="text-medium-emphasis">No scan yet</span>
                        </template>
                        <template #item.compliance_deadline="{item}">
                            <v-chip size="x-small" :color="deadlineColor(item.days_to_deadline)" variant="flat">
                                {{ deadlineLabel(item.days_to_deadline) }}
                            </v-chip>
                        </template>
                        <template #no-data>
                            <div class="py-8 text-center text-medium-emphasis">
                                No enrolled vehicles yet. Use Enroll assets to get started.
                            </div>
                        </template>
                    </v-data-table>
                </v-card-text>
            </v-card>
        </template>

        <CtcEnrollDialog v-model="enrollOpen" @enrolled="onEnrolled"/>
    </v-container>
</template>

<script setup>
import {computed, onMounted, ref} from 'vue';
import {useRouter} from 'vue-router';
import {send} from '@/composables/apiClient';
import {useSnackbarStore} from '@/stores/snackBarStore';
import {formatDateTime} from '@/composables/useTimezone';
import {useCtcStatus} from '@/composables/useCtcStatus';
import {canActOnCompliance} from '@/composables/globals';
import {useAuthStore} from '@/stores/authStore';
import CtcEnrollDialog from '@/components/dialogs/ctc/EnrollDialog.vue';

const router = useRouter();
const {showSnackbar} = useSnackbarStore();
const {user} = useAuthStore();
const {statusColor, statusIcon, deadlineColor, deadlineLabel} = useCtcStatus();
const canEnroll = computed(() => canActOnCompliance(user));

const loading = ref(true);
const summary = ref({readiness_pct: 0, total_enrolled: 0, counts: {}});
const rows = ref([]);
const search = ref('');
const statusFilter = ref(null);
const detailsFilter = ref(false);
const enrollOpen = ref(false);

const headers = [
    {title: 'Asset', key: 'asset_name'},
    {title: 'Driver', key: 'driver'},
    {title: 'VIN', key: 'vin'},
    {title: 'License plate', key: 'license_plate'},
    {title: 'Status', key: 'status'},
    {title: 'Last submission', key: 'last_submission', sortable: false},
    {title: 'Deadline', key: 'compliance_deadline'},
];

const BUCKETS = [
    {key: 'compliant', label: 'Compliant', color: 'success', icon: 'mdi-check-circle'},
    {key: 'due_soon', label: 'Due soon', color: 'warning', icon: 'mdi-clock-alert-outline'},
    {key: 'action_needed', label: 'Action needed', color: 'error', icon: 'mdi-alert-octagon-outline'},
    {key: 'submitting', label: 'Submitting', color: 'info', icon: 'mdi-sync'},
    {key: 'awaiting', label: 'Awaiting scan', color: 'info', icon: 'mdi-progress-clock'},
    {key: 'not_applicable', label: 'Not applicable', color: 'grey', icon: 'mdi-minus-circle-outline'},
];

const readinessColor = computed(() => {
    const pct = summary.value.readiness_pct;
    if (pct >= 90) return 'success';
    if (pct >= 70) return 'warning';
    return 'error';
});

const buckets = computed(() => BUCKETS.map(b => ({...b, count: summary.value.counts?.[b.key] ?? 0})));

const needsAttention = computed(() =>
    rows.value
        .filter(r => r.severity === 'error' || r.severity === 'warning')
        .sort((a, b) => (a.days_to_deadline ?? 9999) - (b.days_to_deadline ?? 9999))
        .slice(0, 8)
);

const needsDetails = computed(() => rows.value.filter(r => r.details_complete === false));

const filtered = computed(() => {
    const q = search.value.trim().toLowerCase();
    return rows.value.filter(r => {
        if (statusFilter.value && r.status !== statusFilter.value) return false;
        if (detailsFilter.value && r.details_complete !== false) return false;
        if (!q) return true;
        return (r.asset_name || '').toLowerCase().includes(q)
            || (r.vin || '').toLowerCase().includes(q)
            || (r.driver || '').toLowerCase().includes(q)
            || (r.license_plate || '').toLowerCase().includes(q);
    });
});

const bucketLabel = (key) => BUCKETS.find(b => b.key === key)?.label ?? key;
const toggleBucket = (key) => statusFilter.value = statusFilter.value === key ? null : key;
const goToAsset = (assetId) => router.push({name: 'ctc-asset', params: {asset: assetId}});
const goSubmissions = () => router.push({name: 'ctc-submissions'});
const onRowClick = (_, {item}) => goToAsset(item.asset_id);

const load = () => {
    loading.value = true;
    Promise.all([
        send('get', 'ctc/summary'),
        send('get', 'ctc/enrollments'),
    ]).then(([summaryRes, listRes]) => {
        summary.value = summaryRes.data.data;
        rows.value = listRes.data.data;
    }).catch(() => showSnackbar('Compliance data could not be loaded', 'error'))
        .finally(() => loading.value = false);
};

const onEnrolled = (ids) => {
    showSnackbar('Assets enrolled', 'success');
    load();
    if (Array.isArray(ids) && ids.length === 1) {
        goToAsset(ids[0]);
    }
};

onMounted(load);
</script>

<style scoped>
.bucket-tile {
    cursor: pointer;
    transition: background-color 0.15s ease;
}

.bucket-tile:hover, .bucket-active {
    background-color: rgba(var(--v-theme-primary), 0.08);
}

.ctc-table :deep(tbody tr) {
    cursor: pointer;
}
</style>
